import React, { useCallback, useEffect, useMemo, useState } from "react";
import { applyToLeague, fetchLeagues, fetchGames } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BoostBar } from "./BoostBar";
import { GameMeta } from "./GameMeta";

export function MatchmakingSection({ onToast, onRequireAuth, onOpenLeague }) {
  const { user } = useAuth();
  const [leagues, setLeagues] = useState([]);
  const [game, setGame] = useState("");
  const [type, setType] = useState("");
  const [skillLevel, setSkillLevel] = useState("");
  const [region, setRegion] = useState("");
  const [openRegOnly, setOpenRegOnly] = useState(false);
  const [recommendedForMe, setRecommendedForMe] = useState(false);
  const [search, setSearch] = useState("");
  const [games, setGames] = useState([]);
  // Default loading=true so the first paint shows the spinner instead of
  // a misleading "No leagues match your filters" — the load() effect
  // below fires immediately on mount, so we're always loading at t=0.
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchGames().then(setGames); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (game) params.game = game;
      if (type) params.league_type = type;
      if (skillLevel) params.skill_level = skillLevel;
      if (region) params.region = region;
      if (openRegOnly) params.open_registration = true;
      if (recommendedForMe && user) params.recommended_for_me = true;
      const data = await fetchLeagues(params);
      setLeagues(data);
    } finally {
      setLoading(false);
    }
  }, [game, type, skillLevel, region, openRegOnly, recommendedForMe, user]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return leagues;
    const q = search.trim().toLowerCase();
    return leagues.filter((l) =>
      l.name.toLowerCase().includes(q)
      || l.game.toLowerCase().includes(q)
      || (l.description || "").toLowerCase().includes(q)
    );
  }, [leagues, search]);

  const apply = async (league) => {
    if (!user) { onRequireAuth(); return; }
    try {
      const r = await applyToLeague(league.league_id);
      onToast(`✅ Applied to ${r.league}. Owner notified.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Application failed.";
      onToast(msg);
    }
  };

  return (
    <section
      id="matchmaking"
      className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-20 page-z"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <span className="px-4 py-1.5 text-[10px] font-bold tracking-[3px] bg-[#14b8a6]/15 text-[#14b8a6] rounded-full neon-border-[#14b8a6]">
            NEON MATCHMAKING
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-display grad-text mt-5 leading-tight">
            FIND YOUR<br />PERFECT LEAGUE
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-[#a3a3ac] mt-4 max-w-xl">
            Search & filter live leagues. PRO priority on premium tier.
          </p>
        </div>
      </div>

      {/* Filters / Search */}
      <div className="glass rounded-3xl p-4 mb-4 grid md:grid-cols-12 gap-3 items-center" data-testid="smart-finder-filters">
        <div className="md:col-span-6 relative">
          <i className="fas fa-search absolute left-5 top-1/2 -translate-y-1/2 text-[#14b8a6]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="league-search-input"
            placeholder="Search by name, game, or description..."
            className="w-full bg-black/40 border border-[#14b8a6]/30 focus:border-[#14b8a6] outline-none rounded-3xl pl-12 pr-5 py-3.5 text-white"
          />
        </div>
        <div className="md:col-span-3">
          <select
            value={game}
            onChange={(e) => setGame(e.target.value)}
            data-testid="match-game-filter"
            className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-3xl px-5 py-3.5 text-white font-bold tracking-wider"
          >
            <option value="">ALL GAMES</option>
            {games.map((g) => <option key={g.slug} value={g.name}>{g.name}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            data-testid="match-type-filter"
            className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-3xl px-5 py-3.5 text-white font-bold tracking-wider"
          >
            <option value="">ALL TIERS</option>
            <option value="Casual">CASUAL</option>
            <option value="PRO">PRO</option>
          </select>
        </div>
        <div className="md:col-span-1 text-right text-xs tracking-widest text-[#14b8a6]" data-testid="results-count">
          {filtered.length} HITS
        </div>
      </div>

      {/* Smart Finder — skill level chips, region, open-only, recommended */}
      <div className="glass rounded-3xl p-4 mb-8 flex flex-wrap items-center gap-3" data-testid="smart-finder-row">
        <span className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mr-1">
          <i className="fas fa-wand-magic-sparkles mr-1" />SMART FINDER
        </span>
        <div className="flex gap-1" data-testid="smart-finder-skill">
          {[
            { id: "",            label: "ANY" },
            { id: "casual",      label: "🟢 CASUAL" },
            { id: "rising",      label: "🟡 RISING" },
            { id: "competitive", label: "🔴 COMPETITIVE" },
          ].map((s) => (
            <button
              key={s.id || "any"}
              type="button"
              onClick={() => setSkillLevel(s.id)}
              data-testid={`smart-finder-skill-${s.id || "any"}`}
              className={`text-[10px] tracking-widest px-3 py-1.5 rounded-lg border transition ${
                skillLevel === s.id
                  ? "bg-[#14b8a6]/20 text-[#14b8a6] border-[#14b8a6]/60"
                  : "bg-black/30 text-white/65 border-white/15 hover:border-white/35"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <select
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          data-testid="smart-finder-region"
          className="bg-black/40 border border-white/20 rounded-lg px-3 py-1.5 text-[11px] text-white"
        >
          <option value="">ANY REGION</option>
          {["NA", "EU", "LATAM", "ASIA", "OCE", "MEA"].map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-[11px] text-white/75 cursor-pointer">
          <input
            type="checkbox"
            checked={openRegOnly}
            onChange={(e) => setOpenRegOnly(e.target.checked)}
            data-testid="smart-finder-open"
            className="accent-[#14b8a6]"
          />
          OPEN FOR REGISTRATION
        </label>
        {user && (
          <label className="flex items-center gap-1 text-[11px] text-[#14b8a6] cursor-pointer">
            <input
              type="checkbox"
              checked={recommendedForMe}
              onChange={(e) => setRecommendedForMe(e.target.checked)}
              data-testid="smart-finder-recommended"
              className="accent-[#14b8a6]"
            />
            ★ RECOMMENDED FOR ME
          </label>
        )}
        {(skillLevel || region || openRegOnly || recommendedForMe) && (
          <button
            type="button"
            onClick={() => {
              setSkillLevel(""); setRegion(""); setOpenRegOnly(false); setRecommendedForMe(false);
            }}
            data-testid="smart-finder-clear"
            className="text-[10px] text-white/50 hover:text-white tracking-widest ml-auto"
          >
            CLEAR <i className="fas fa-xmark ml-1" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="loader-ring" /></div>
      ) : filtered.length === 0 ? (
        <div className="glass rounded-3xl p-10 text-center">
          <p className="text-xl text-white/60">No leagues match your filters.</p>
          <p className="text-sm text-white/40 mt-2">Try clearing the search or filter.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="leagues-grid">
          {filtered.map((l) => {
            const isPro = l.league_type === "PRO";
            return (
            <div
              key={l.league_id}
              data-testid={`league-card-${l.league_id}`}
              onClick={() => onOpenLeague?.(l.league_id)}
              className={`card-3d glass rounded-3xl relative cursor-pointer overflow-hidden ${
                l.superboost_active ? "border-2 border-[#14b8a6] shadow-[0_0_28px_rgba(253,224,71,0.45)] animate-glow-pulse" :
                isPro ? "border-2 border-[#14b8a6]/70 shadow-[0_0_24px_rgba(250,204,21,0.30)]" : ""
              }`}
            >
              {l.superboost_active && (
                <div
                  data-testid={`league-superboost-ribbon-${l.league_id}`}
                  className="absolute top-2 left-2 z-10 text-[9px] tracking-[3px] font-bold px-2 py-0.5 rounded-md bg-[#14b8a6] text-black shadow"
                  title="Superboosted — pinned to the top of /leagues for 24h"
                >
                  <i className="fas fa-bolt mr-1" />SUPERBOOSTED
                </div>
              )}
              {isPro && l.banner_url && (
                <img src={l.banner_url} alt="" className="w-full h-24 object-cover border-b border-[#14b8a6]/30"
                     onError={(e) => { e.currentTarget.style.display = "none"; }} />
              )}
              <div className="p-6">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-2xl font-display grad-text leading-tight flex items-center gap-2 min-w-0 truncate">
                  {isPro && (
                    <span className="text-[#14b8a6] text-2xl shrink-0">{l.pro_badge || "👑"}</span>
                  )}
                  <span className="truncate">{l.name}</span>
                </h3>
                <span
                  className={`text-[10px] font-bold tracking-widest px-3 py-1 rounded-full whitespace-nowrap ${
                    isPro
                      ? "bg-[#14b8a6] text-black"
                      : "bg-[#14b8a6]/20 text-[#14b8a6] border border-[#14b8a6]/40"
                  }`}
                >
                  {isPro ? "👑 PRO" : "CASUAL"}
                </span>
              </div>
              <div className="mt-2">
                <GameMeta gameName={l.game} games={games} dataTestid={`discover-game-meta-${l.league_id}`} />
              </div>
              <p className="text-sm text-white/60 mt-2 line-clamp-2">{l.description || "Active league. Apply now."}</p>
              {isPro && l.pro_perks?.length > 0 && (
                <ul className="mt-2 space-y-0.5">
                  {l.pro_perks.slice(0, 2).map((p, i) => (
                    <li key={i} className="text-[11px] text-[#14b8a6]/90 truncate">★ {p}</li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-2 mt-5 items-center">
                <span className="text-xs bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-3 py-1">
                  MMR {l.skill_min}–{l.skill_max}
                </span>
                <span className="text-xs bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-3 py-1">
                  {l.spots_open}/{l.spots_total} OPEN
                </span>
                <BoostBar leagueId={l.league_id} compact onToast={onToast} />
              </div>
              <div className="mt-6 grid grid-cols-2 gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); onOpenLeague?.(l.league_id); }}
                  data-testid={`view-btn-${l.league_id}`}
                  className="py-3 btn-ghost rounded-2xl font-bold tracking-widest text-sm"
                >
                  VIEW
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); apply(l); }}
                  data-testid={`apply-btn-${l.league_id}`}
                  disabled={l.spots_open <= 0}
                  className="py-3 btn-neon rounded-2xl disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {l.spots_open <= 0 ? "FULL" : "APPLY"}
                </button>
              </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default MatchmakingSection;
